// AgentActivity (Domain/Inbox; was HomeViews.tsx's, HIERARCHY follow-up 8, BL-189): what the agents did
// today (agents.activity), one step a row on hairlines: what it did, which agent, when and how it went;
// at the end, how it was decided, or Done, Waiting for you, Refused or Failed. Five at most (four in a
// chat), then how many more; with a step still waiting, one way in: Decide what waits. A page whose
// section already names it (Home's What the agents did today) passes titled={false}. Reads only
// --agentlog-*; Label, Button and Link read their own.
import type { AgentActivityProps } from '../../schemas.ts';
import type { ViewProps } from '../../host.ts';
import { day, plural } from '../../format.ts';
import { Label, type LabelKind } from '../Label/Label.tsx';
import { agentName, agentTime, ViewAct } from '../view-act.tsx';
import './AgentActivity.css';

const MODE: Record<AgentActivityProps['steps'][number]['mode'], [string, LabelKind]> = {
  automatic: ['Done', 'muted'],
  asked: ['Waiting for you', 'wait'],
  refused: ['Refused', 'bad'],
  failed: ['Failed', 'bad'],
};

const DECIDED = { approved: 'Approved', declined: 'Declined', expired: 'Expired' } as const;

/** agents.activity */
export function AgentActivity({
  data,
  host,
  titled = true,
}: ViewProps<AgentActivityProps> & {
  /** Its own title, What the agents did; false where the section over it already says so. */
  titled?: boolean;
}) {
  const chat = host.surface === 'chat';
  const shown = data.steps.slice(0, chat ? 4 : 5);
  // asked and still undecided: one a person already decided waits for nobody (QA-858)
  const waiting = data.steps.filter((s) => s.mode === 'asked' && !s.decision).length;
  if (!data.steps.length)
    return (
      <p className="fl-agentlog__empty" role="status">
        The agents haven’t done anything {data.date ? `on ${day(data.date)}` : 'today'}.
      </p>
    );
  return (
    <article className="fl-agentlog" data-surface={host.surface} aria-label="What the agents did">
      {titled && <h3 className="fl-agentlog__title">What the agents did</h3>}
      <p className="fl-agentlog__sub">
        {plural(data.steps.length, 'step')}
        {waiting ? `, ${waiting} waiting for you` : ''}
      </p>
      <ul className="fl-agentlog__rows">
        {shown.map((s) => (
          <li key={`${s.runId}-${s.n}`} className="fl-agentlog__row">
            <span>
              <b>{s.actionTitle ?? s.task}</b>
              <span className="fl-agentlog__sub">
                {agentName(s.agentKey)}, {agentTime(s.at)} {s.outcome}
              </span>
            </span>
            {s.decision ? (
              <Label kind={s.decision.status === 'approved' ? 'muted' : 'bad'}>
                {DECIDED[s.decision.status]}
                {s.decision.by && s.decision.status !== 'expired' ? ` by ${s.decision.by}` : ''}
              </Label>
            ) : (
              <Label kind={MODE[s.mode][1]}>{MODE[s.mode][0]}</Label>
            )}
          </li>
        ))}
      </ul>
      {data.steps.length > shown.length && (
        <p className="fl-agentlog__sub">And {data.steps.length - shown.length} more.</p>
      )}
      {waiting > 0 && (
        <div className="fl-agentlog__acts">
          <ViewAct surface={host.surface} primary={!chat} onPress={() => host.open({ kind: 'approvals' })}>
            {chat ? 'Open in Flowology to approve' : 'Decide what waits'}
          </ViewAct>
        </div>
      )}
    </article>
  );
}
